/**
 * ContractsTab - View supplier contracts and negotiate new ones
 */

import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  FUEL_SUPPLIERS,
  getAvailableSuppliers,
  isSupplierAvailable,
  formatFuelPrice,
  formatGallons,
} from "../../systems/FuelSystem.js";

// Tier badge colors
const TIER_COLORS = {
  REGIONAL: { bg: "#1a3d1a", border: "#22c55e", text: "#4ade80" },
  MAJOR: { bg: "#1a2d4d", border: "#3b82f6", text: "#60a5fa" },
  DISCOUNT: { bg: "#3d3d1a", border: "#eab308", text: "#facc15" },
  PREMIUM: { bg: "#3d1a3d", border: "#a855f7", text: "#c084fc" },
};

function SupplierCard({ supplier, isUnlocked, hasActiveContract, onNegotiate }) {
  const [expanded, setExpanded] = useState(false);
  const tierColors = TIER_COLORS[supplier.tier] || TIER_COLORS.REGIONAL;

  return (
    <div
      style={{
        backgroundColor: "#111",
        borderRadius: "8px",
        border: `1px solid ${isUnlocked ? "#1a1a1a" : "#2a2a2a"}`,
        opacity: isUnlocked ? 1 : 0.5,
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: "16px",
          cursor: "pointer",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" }}>
            <span style={{ color: "#e5e5e5", fontWeight: "600", fontSize: "15px" }}>
              {supplier.name}
            </span>
            <span
              style={{
                padding: "2px 8px",
                borderRadius: "4px",
                fontSize: "10px",
                fontWeight: "bold",
                backgroundColor: tierColors.bg,
                border: `1px solid ${tierColors.border}`,
                color: tierColors.text,
              }}
            >
              {supplier.tier}
            </span>
            {hasActiveContract && (
              <span
                style={{
                  padding: "2px 8px",
                  borderRadius: "4px",
                  fontSize: "10px",
                  fontWeight: "bold",
                  backgroundColor: "#1a3d1a",
                  border: "1px solid #22c55e",
                  color: "#4ade80",
                }}
              >
                ACTIVE
              </span>
            )}
            {!isUnlocked && (
              <span
                style={{
                  padding: "2px 8px",
                  borderRadius: "4px",
                  fontSize: "10px",
                  fontWeight: "bold",
                  backgroundColor: "#2a2a2a",
                  color: "#737373",
                }}
              >
                TIER {supplier.unlockedAtTier}
              </span>
            )}
          </div>
          <div style={{ color: "#737373", fontSize: "12px", marginBottom: "8px" }}>
            {supplier.description}
          </div>

          {/* Key stats */}
          <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
            <div>
              <span style={{ color: "#525252", fontSize: "11px" }}>BASE RATE</span>
              <div style={{ color: "#60ffd0", fontFamily: "monospace", fontWeight: "bold" }}>
                ${supplier.baseRate.toFixed(2)}/gal
              </div>
            </div>
            <div>
              <span style={{ color: "#525252", fontSize: "11px" }}>RELIABILITY</span>
              <div style={{ color: supplier.reliability >= 0.95 ? "#22c55e" : "#eab308", fontFamily: "monospace" }}>
                {(supplier.reliability * 100).toFixed(1)}%
              </div>
            </div>
            <div>
              <span style={{ color: "#525252", fontSize: "11px" }}>COVERAGE</span>
              <div style={{ color: "#e5e5e5", fontSize: "12px" }}>
                {supplier.coverage ? `${supplier.coverage.length} airports` : "National"}
              </div>
            </div>
            {supplier.maintenanceBonus !== 0 && (
              <div>
                <span style={{ color: "#525252", fontSize: "11px" }}>MAINT BONUS</span>
                <div style={{ color: "#22c55e", fontSize: "12px" }}>
                  {(supplier.maintenanceBonus * 100).toFixed(0)}%
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Expand indicator */}
        <div style={{ color: "#525252", fontSize: "12px", marginLeft: "12px" }}>
          {expanded ? "▼" : "▶"}
        </div>
      </div>

      {/* Expanded details */}
      {expanded && (
        <div
          style={{
            padding: "0 16px 16px 16px",
            borderTop: "1px solid #1a1a1a",
            paddingTop: "16px",
          }}
        >
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", marginBottom: "16px" }}>
            {/* Strengths */}
            <div>
              <div style={{ color: "#22c55e", fontSize: "11px", fontWeight: "600", marginBottom: "8px" }}>
                STRENGTHS
              </div>
              <ul style={{ margin: 0, paddingLeft: "16px", color: "#a1a1a1", fontSize: "12px", lineHeight: "1.6" }}>
                {supplier.strengths.map((s, i) => (
                  <li key={i}>{s}</li>
                ))}
              </ul>
            </div>

            {/* Weaknesses */}
            <div>
              <div style={{ color: "#ef4444", fontSize: "11px", fontWeight: "600", marginBottom: "8px" }}>
                WEAKNESSES
              </div>
              <ul style={{ margin: 0, paddingLeft: "16px", color: "#a1a1a1", fontSize: "12px", lineHeight: "1.6" }}>
                {supplier.weaknesses.map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </ul>
            </div>
          </div>

          {/* Contract terms */}
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: "8px",
            marginBottom: "16px",
            padding: "12px",
            backgroundColor: "#0a0a0a",
            borderRadius: "6px",
          }}>
            <div>
              <div style={{ color: "#525252", fontSize: "10px" }}>MIN DURATION</div>
              <div style={{ color: "#e5e5e5", fontSize: "13px" }}>{supplier.minContractMonths}mo</div>
            </div>
            <div>
              <div style={{ color: "#525252", fontSize: "10px" }}>MAX DURATION</div>
              <div style={{ color: "#e5e5e5", fontSize: "13px" }}>{supplier.maxContractMonths}mo</div>
            </div>
            <div>
              <div style={{ color: "#525252", fontSize: "10px" }}>MIN ALLOC</div>
              <div style={{ color: "#e5e5e5", fontSize: "13px" }}>{formatGallons(supplier.minAllocationGal)}</div>
            </div>
            <div>
              <div style={{ color: "#525252", fontSize: "10px" }}>MAX ALLOC</div>
              <div style={{ color: "#e5e5e5", fontSize: "13px" }}>{formatGallons(supplier.maxAllocationGal)}</div>
            </div>
          </div>

          {/* Coverage airports */}
          {supplier.coverage && (
            <div style={{ marginBottom: "16px" }}>
              <div style={{ color: "#525252", fontSize: "11px", marginBottom: "4px" }}>COVERED AIRPORTS</div>
              <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                {supplier.coverage.map((apt) => (
                  <span
                    key={apt}
                    style={{
                      padding: "2px 6px",
                      backgroundColor: "#1a1a1a",
                      borderRadius: "4px",
                      color: "#a1a1a1",
                      fontSize: "11px",
                      fontFamily: "monospace",
                    }}
                  >
                    {apt}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Negotiate button */}
          {isUnlocked && (
            <Button
              onClick={() => onNegotiate(supplier.id)}
              style={{
                width: "100%",
                backgroundColor: hasActiveContract ? "#1a2d4d" : "#1a3d1a",
                borderColor: hasActiveContract ? "#3b82f6" : "#22c55e",
                color: hasActiveContract ? "#60a5fa" : "#4ade80",
              }}
            >
              {hasActiveContract ? "RENEGOTIATE CONTRACT" : "NEGOTIATE CONTRACT"}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

function ActiveContractCard({ contract }) {
  const [expanded, setExpanded] = useState(false);
  const supplier = FUEL_SUPPLIERS[contract.supplierId];

  // Calculate remaining days
  const endDate = new Date(contract.endDate);
  const now = new Date();
  const daysRemaining = Math.max(0, Math.ceil((endDate - now) / (1000 * 60 * 60 * 24)));

  // Usage percentage
  const usagePercent = (contract.usedThisMonth / contract.monthlyAllocationGal) * 100;
  const minimumPercent = (contract.minimumPurchaseGal / contract.monthlyAllocationGal) * 100;

  return (
    <div
      style={{
        backgroundColor: "#111",
        borderRadius: "8px",
        border: "1px solid #22c55e40",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          padding: "16px",
          cursor: "pointer",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" }}>
            <span style={{ color: "#e5e5e5", fontWeight: "600", fontSize: "14px" }}>
              {contract.supplierName}
            </span>
            <span
              style={{
                padding: "2px 8px",
                borderRadius: "4px",
                fontSize: "10px",
                fontWeight: "bold",
                backgroundColor: "#1a3d1a",
                border: "1px solid #22c55e",
                color: "#4ade80",
              }}
            >
              ACTIVE
            </span>
          </div>
          <div style={{ display: "flex", gap: "16px", marginTop: "8px" }}>
            <div>
              <span style={{ color: "#525252", fontSize: "10px" }}>RATE</span>
              <div style={{ color: "#60ffd0", fontFamily: "monospace", fontWeight: "bold", fontSize: "14px" }}>
                ${contract.negotiatedRate.toFixed(2)}
              </div>
            </div>
            <div>
              <span style={{ color: "#525252", fontSize: "10px" }}>DAYS LEFT</span>
              <div style={{ color: daysRemaining < 30 ? "#eab308" : "#e5e5e5", fontSize: "14px" }}>
                {daysRemaining}
              </div>
            </div>
            <div>
              <span style={{ color: "#525252", fontSize: "10px" }}>USED/ALLOC</span>
              <div style={{ color: "#e5e5e5", fontSize: "14px" }}>
                {formatGallons(contract.usedThisMonth)}/{formatGallons(contract.monthlyAllocationGal)}
              </div>
            </div>
          </div>
        </div>
        <div style={{ color: "#525252", fontSize: "12px" }}>
          {expanded ? "▼" : "▶"}
        </div>
      </div>

      {expanded && (
        <div style={{ padding: "0 16px 16px 16px", borderTop: "1px solid #1a1a1a", paddingTop: "16px" }}>
          {/* Usage bar */}
          <div style={{ marginBottom: "16px" }}>
            <div style={{ color: "#525252", fontSize: "11px", marginBottom: "6px" }}>MONTHLY USAGE</div>
            <div style={{ position: "relative", height: "12px", backgroundColor: "#1a1a1a", borderRadius: "6px", overflow: "hidden" }}>
              {/* Minimum marker */}
              <div
                style={{
                  position: "absolute",
                  left: `${minimumPercent}%`,
                  top: 0,
                  bottom: 0,
                  width: "2px",
                  backgroundColor: "#eab308",
                  zIndex: 1,
                }}
              />
              {/* Usage fill */}
              <div
                style={{
                  width: `${Math.min(100, usagePercent)}%`,
                  height: "100%",
                  backgroundColor: usagePercent >= minimumPercent ? "#22c55e" : "#ef4444",
                  transition: "width 0.3s",
                }}
              />
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: "4px" }}>
              <span style={{ color: "#737373", fontSize: "10px" }}>Min: {formatGallons(contract.minimumPurchaseGal)}</span>
              <span style={{ color: "#737373", fontSize: "10px" }}>Max: {formatGallons(contract.monthlyAllocationGal)}</span>
            </div>
          </div>

          {/* Details grid */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "12px", marginBottom: "16px" }}>
            <div>
              <div style={{ color: "#525252", fontSize: "10px" }}>RELIABILITY</div>
              <div style={{ color: contract.reliability >= 0.95 ? "#22c55e" : "#eab308", fontSize: "13px" }}>
                {(contract.reliability * 100).toFixed(1)}%
              </div>
            </div>
            <div>
              <div style={{ color: "#525252", fontSize: "10px" }}>RELATIONSHIP</div>
              <div style={{ color: contract.relation >= 70 ? "#22c55e" : contract.relation >= 40 ? "#eab308" : "#ef4444", fontSize: "13px" }}>
                {contract.relation}/100
              </div>
            </div>
            {contract.maintenanceBonus !== 0 && (
              <div>
                <div style={{ color: "#525252", fontSize: "10px" }}>MAINT BONUS</div>
                <div style={{ color: "#22c55e", fontSize: "13px" }}>
                  {(contract.maintenanceBonus * 100).toFixed(0)}%
                </div>
              </div>
            )}
          </div>

          {/* Coverage */}
          {contract.coverageAirports && (
            <div>
              <div style={{ color: "#525252", fontSize: "10px", marginBottom: "4px" }}>COVERAGE</div>
              <div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
                {contract.coverageAirports.slice(0, 6).map((apt) => (
                  <span
                    key={apt}
                    style={{
                      padding: "2px 6px",
                      backgroundColor: "#1a1a1a",
                      borderRadius: "4px",
                      color: "#a1a1a1",
                      fontSize: "10px",
                      fontFamily: "monospace",
                    }}
                  >
                    {apt}
                  </span>
                ))}
                {contract.coverageAirports.length > 6 && (
                  <span style={{ color: "#525252", fontSize: "10px" }}>
                    +{contract.coverageAirports.length - 6} more
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function ContractsTab({ fuelState, reputationTier, onStartNegotiation }) {
  const contracts = fuelState?.contracts || [];
  const activeContracts = contracts.filter((c) => c.status === "active");
  const availableSuppliers = getAvailableSuppliers(reputationTier);

  // Get suppliers with active contracts
  const suppliersWithContracts = new Set(activeContracts.map((c) => c.supplierId));

  return (
    <div>
      {/* Active Contracts Section */}
      {activeContracts.length > 0 && (
        <div style={{ marginBottom: "24px" }}>
          <div style={{ marginBottom: "12px", color: "#737373", fontSize: "12px", fontWeight: "600" }}>
            ACTIVE CONTRACTS ({activeContracts.length})
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            {activeContracts.map((contract) => (
              <ActiveContractCard key={contract.id} contract={contract} />
            ))}
          </div>
        </div>
      )}

      {/* Available Suppliers Section */}
      <div>
        <div style={{ marginBottom: "12px", color: "#737373", fontSize: "12px", fontWeight: "600" }}>
          FUEL SUPPLIERS
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          {Object.values(FUEL_SUPPLIERS).map((supplier) => (
            <SupplierCard
              key={supplier.id}
              supplier={supplier}
              isUnlocked={isSupplierAvailable(supplier.id, reputationTier)}
              hasActiveContract={suppliersWithContracts.has(supplier.id)}
              onNegotiate={onStartNegotiation}
            />
          ))}
        </div>
      </div>

      {/* Info footer */}
      <div
        style={{
          marginTop: "24px",
          padding: "16px",
          backgroundColor: "#0a0a0a",
          borderRadius: "8px",
          border: "1px solid #1a1a1a",
        }}
      >
        <div style={{ color: "#737373", fontSize: "12px", fontWeight: "600", marginBottom: "8px" }}>
          ABOUT SUPPLIER CONTRACTS
        </div>
        <div style={{ color: "#a1a1a1", fontSize: "13px", lineHeight: "1.6" }}>
          Supplier contracts lock in fuel rates below spot price for covered airports.
          You must meet monthly minimum purchase requirements or face penalties.
          Better relationships lead to better renewal terms.
        </div>
      </div>
    </div>
  );
}

export default ContractsTab;
